/**
 * Platform: tenant-overzicht (alleen superadmin)
 */
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

export function AdminTenants() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => api.listAdminTenants(),
    enabled: isSuper,
  });

  if (!isSuper) {
    return <Navigate to="/admin" replace />;
  }

  const errMsg = error instanceof Error ? error.message : String(error ?? '');

  return (
    <div>
      <SeoMeta
        path="/admin/tenants"
        noindex
        title="Tenants"
        description="Tenant- en klantoverzicht CalqTool™: organisaties, gebruikers en tellingen — strikt superadmin."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Tenants' }]} />
      <div className="mb-6 flex items-center gap-2">
        <Building2 className="h-6 w-6 text-primary" aria-hidden />
        <h1 className="text-2xl font-bold text-text">Tenants</h1>
      </div>
      <p className="mb-6 text-sm text-text-muted">
        Overzicht van alle tenants met gebruikers- en projecttellingen. Alleen zichtbaar in{' '}
        <strong className="text-text">Platformbeheer</strong>.
      </p>

      {isLoading && <p className="text-text-muted">Laden…</p>}
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {errMsg}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-surface">
              <tr>
                <th className="p-2">Tenant</th>
                <th className="p-2">Naam</th>
                <th className="p-2">Billing</th>
                <th className="p-2">Plan (sub)</th>
                <th className="p-2">Users</th>
                <th className="p-2">Projecten</th>
                <th className="p-2">Aangemaakt</th>
              </tr>
            </thead>
            <tbody>
              {(data.tenants ?? []).map((t) => (
                <tr key={t.tenant_id} className="border-t border-border">
                  <td className="p-2 font-mono text-xs">{t.tenant_id}</td>
                  <td className="p-2">{t.name}</td>
                  <td className="p-2 text-text-muted">{t.billing_status || '—'}</td>
                  <td className="p-2 text-text-muted">{t.subscription_plan_id || t.billing_plan_label || '—'}</td>
                  <td className="p-2">{t.user_count}</td>
                  <td className="p-2">{t.project_count}</td>
                  <td className="p-2 text-text-muted">{new Date(t.created_at).toLocaleDateString('nl-NL')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
